import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IRackingRuleSortItem, IRacksets, IRule, RackingService } from '@shared/services/racking.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';

type FormValidators = Array<(control: AbstractControl) => ValidationErrors | null>;

@Component({
	selector: 'app-racking-create-update-rule',
	templateUrl: './racking-create-update-rule.html',
	styleUrls: ['./racking-create-update-rule.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class RackingCreateUpdateRuleDialogComponent implements OnInit {
	@Output() saved = new EventEmitter<void>();
	public loading = false;
	public httpError = '';
	public availableRacksets: IRacksets;
	public states: ['Any', 'NoGeometry', 'OnlyGeometry'] = ['Any', 'NoGeometry', 'OnlyGeometry'];

	// form
	public form: FormGroup;
	public sortingPriorityForm: FormGroup;
	// form control variables
	public sortingProfileLength = false;
	public sortingProfileLengthOrder = false;
	public sortingProfileLengthPriority: number | null;
	public sortingProductionSequenceNumber = false;
	public sortingProductionSequenceNumberOrder = false;
	public sortingProductionSequenceNumberPriority: number | null;
	public sortingModelIndex = false;
	public sortingModelIndexOrder = false;
	public sortingModelIndexPriority: number | null;
	public sortingSequenceNumber = false;
	public sortingSequenceNumberOrder = false;
	public sortingSequenceNumberPriority: number | null;
	public sortingComponent = false;
	public sortingComponentOrder = false;
	public sortingComponentPriority: number | null;
	public sortingOperation = false;
	public sortingOperationOrder = false;
	public sortingOperationPriority: number | null;
	public isFormValid = false;
	public notuniqueError = false;
	public profileLengthMaxError = false;
	public productionSequenceMaxError = false;
	public modelIndexMaxError = false;
	public sequenceNumberMaxError = false;
	public componentMaxError = false;
	public operationMaxError = false;

	private rule: IRule;

	constructor(
		@Inject(MAT_DIALOG_DATA) public editRule: IRule,
		public dialogRef: MatDialogRef<RackingCreateUpdateRuleDialogComponent>,
		private rackingService: RackingService,
		private fb: FormBuilder,
	) {
		this.rule = {
			self: null,
			name: null,
			description: null,
			batchType: null,
			useCoatingInOptimization: false,
			profile: null,
			groupByProductionRoute: false,
			productionSequenceNumberMode: 'Disabled',
			slotsPerCell: 1,
			slotConfiguration: 'MixElements',
			rackset: null,
			placementPriority: null,
			segregation: null,
			optimization: null,
			sorting: [],
			updatedDate: null,
			updatedBy: null,
			state: null,
			material: '',
			machine: '',
			id0: null,
			id1: null,
			id2: null,
			id3: null,
			id4: null,
			id5: null,
			id6: null,
			id7: null,
			id8: null,
			id9: null,
			logisticsFormula: null,
		};

		if (editRule) {
			this.rule = { ...editRule };
		}

		if (!this.rule.state) {
			this.rule.state = 'Any';
		}
	}

	public ngOnInit(): void {
		this.rackingService.getRacksets().subscribe((racksets) => {
			this.availableRacksets = racksets;
		});
		this.initializeForm();
		this.setViewModelFromServerConfiguration();
	}

	private setViewModelFromServerConfiguration(): void {
		this.setViewModelSortingFromServerConfiguration();
		this.setEnableDisablePriorityFormFields();
	}

	private setEnableDisablePriorityFormFields(): void {
		const profileLengthControl = this.sortingPriorityForm.get('sortingProfileLengthPriority');
		if (profileLengthControl) {
			if (this.sortingProfileLength) {
				profileLengthControl.enable();
			} else {
				profileLengthControl.reset();
				profileLengthControl.disable();
			}
		}
		const productionSequenceControl = this.sortingPriorityForm.get('sortingProductionSequenceNumberPriority');
		if (productionSequenceControl) {
			if (this.sortingProductionSequenceNumber) {
				productionSequenceControl.enable();
			} else {
				productionSequenceControl.reset();
				productionSequenceControl.disable();
			}
		}
		const modelIndexControl = this.sortingPriorityForm.get('sortingModelIndexPriority');
		if (modelIndexControl) {
			if (this.sortingModelIndex) {
				modelIndexControl.enable();
			} else {
				modelIndexControl.reset();
				modelIndexControl.disable();
			}
		}
		const sequenceControl = this.sortingPriorityForm.get('sortingSequenceNumberPriority');
		if (sequenceControl) {
			if (this.sortingSequenceNumber) {
				sequenceControl.enable();
			} else {
				sequenceControl.reset();
				sequenceControl.disable();
			}
		}
		const componentControl = this.sortingPriorityForm.get('sortingComponentPriority');
		if (sequenceControl) {
			if (this.sortingComponent) {
				componentControl.enable();
			} else {
				componentControl.reset();
				componentControl.disable();
			}
		}
		const operationControl = this.sortingPriorityForm.get('sortingOperationPriority');
		if (operationControl) {
			if (this.sortingOperation) {
				operationControl.enable();
			} else {
				operationControl.reset();
				operationControl.disable();
			}
		}
	}

	private initializeForm(): void {
		this.form = this.fb.group(
			{
				name: [this.rule.name, [Validators.required, Validators.maxLength(50), Validators.pattern(ValidatorPatterns.AllowedCuttingCharacters)]],
				description: [this.rule.description, [Validators.maxLength(100), Validators.pattern(ValidatorPatterns.DanishCharacters)]],
				batchType: [this.rule.batchType, [Validators.maxLength(255), Validators.pattern(ValidatorPatterns.AllowedCuttingCharacters)]],
				useCoatingInOptimization: [this.rule.useCoatingInOptimization],
				profile: [this.rule.profile || '', [Validators.maxLength(255), Validators.pattern(ValidatorPatterns.AllowedCuttingCharacters)]],
				groupByProductionRoute: [this.rule.groupByProductionRoute],
				productionSequenceNumberMode: [this.rule.productionSequenceNumberMode || 'Disabled', [Validators.required]],
				slotsPerCell: [this.rule.slotsPerCell, [Validators.max(99999)]],
				slotConfiguration: [this.rule.slotConfiguration || 'MixElements', [Validators.required]],
				material: [this.rule.material, [Validators.maxLength(255), Validators.pattern(ValidatorPatterns.AllowedCuttingCharacters), validateAluminium()]],
				machine: [this.rule.machine, [Validators.maxLength(255), Validators.pattern(ValidatorPatterns.AllowedCuttingCharacters), validateAluminium()]],
				state: [this.rule.state],
				rackset: [this.rule.rackset],
				optimization: [this.rule.optimization, [Validators.maxLength(255), Validators.pattern(ValidatorPatterns.AllowedCuttingCharacters)]],
				placementPriority: [this.rule.placementPriority, [Validators.max(99999)]],
				segregation: [this.rule.segregation, [Validators.maxLength(255), Validators.pattern(ValidatorPatterns.AllowedCuttingCharacters)]],
				id0: [this.rule.id0, [Validators.maxLength(255)]],
				id1: [this.rule.id1, [Validators.maxLength(255)]],
				id2: [this.rule.id2, [Validators.maxLength(255)]],
				id3: [this.rule.id3, [Validators.maxLength(255)]],
				id4: [this.rule.id4, [Validators.maxLength(255)]],
				id5: [this.rule.id5, [Validators.maxLength(255)]],
				id6: [this.rule.id6, [Validators.maxLength(255)]],
				id7: [this.rule.id7, [Validators.maxLength(255)]],
				id8: [this.rule.id8, [Validators.maxLength(255)]],
				id9: [this.rule.id9, [Validators.maxLength(255)]],
				logisticsFormula: [this.rule.logisticsFormula ?? '', [Validators.maxLength(1000)]],
			},
			{
				updateOn: 'blur',
			},
		);

		this.sortingPriorityForm = this.fb.group({
			sortingProfileLengthPriority: [this.sortingProfileLengthPriority, this.sortingPriorityFormValidators],
			sortingProductionSequenceNumberPriority: [this.sortingProductionSequenceNumberPriority, this.sortingPriorityFormValidators],
			sortingModelIndexPriority: [this.sortingModelIndexPriority, this.sortingPriorityFormValidators],
			sortingSequenceNumberPriority: [this.sortingSequenceNumberPriority, this.sortingPriorityFormValidators],
			sortingComponentPriority: [this.sortingComponentPriority, this.sortingPriorityFormValidators],
			sortingOperationPriority: [this.sortingOperationPriority, this.sortingPriorityFormValidators],
		});

		this.subscribeToFormChanges();
	}

	private setViewModelSortingFromServerConfiguration(): void {
		// set default values
		this.sortingProfileLength = false;
		this.sortingProfileLengthOrder = false;
		this.sortingProfileLengthPriority = null;

		this.sortingProductionSequenceNumber = false;
		this.sortingProductionSequenceNumberOrder = false;
		this.sortingProductionSequenceNumberPriority = null;

		this.sortingModelIndex = false;
		this.sortingModelIndexOrder = false;
		this.sortingModelIndexPriority = null;

		this.sortingSequenceNumber = false;
		this.sortingSequenceNumberOrder = false;
		this.sortingSequenceNumberPriority = null;

		this.sortingComponent = false;
		this.sortingComponentOrder = false;
		this.sortingComponentPriority = null;

		this.sortingOperation = false;
		this.sortingOperationOrder = false;
		this.sortingOperationPriority = null;

		const sorting = this.rule.sorting;
		for (const sort of sorting) {
			if (sort.type.self === '/racking/configuration-sorting-strategies/profileLength') {
				this.sortingProfileLength = true;
				this.sortingProfileLengthOrder = sort.order === 0;
				this.sortingProfileLengthPriority = sort.priority ? sort.priority : null;
			}
			if (sort.type.self === '/racking/configuration-sorting-strategies/productionSequenceNumber') {
				this.sortingProductionSequenceNumber = true;
				this.sortingProductionSequenceNumberOrder = sort.order === 0;
				this.sortingProductionSequenceNumberPriority = sort.priority ? sort.priority : null;
			}
			if (sort.type.self === '/racking/configuration-sorting-strategies/modelIndex') {
				this.sortingModelIndex = true;
				this.sortingModelIndexOrder = sort.order === 0;
				this.sortingModelIndexPriority = sort.priority ? sort.priority : null;
			}
			if (sort.type.self === '/racking/configuration-sorting-strategies/sequenceNumber') {
				this.sortingSequenceNumber = true;
				this.sortingSequenceNumberOrder = sort.order === 0;
				this.sortingSequenceNumberPriority = sort.priority ? sort.priority : null;
			}

			if (sort.type.self === '/racking/configuration-sorting-strategies/component') {
				this.sortingComponent = true;
				this.sortingComponentOrder = sort.order === 0;
				this.sortingComponentPriority = sort.priority ? sort.priority : null;
			}
			if (sort.type.self === '/racking/configuration-sorting-strategies/operation') {
				this.sortingOperation = true;
				this.sortingOperationOrder = sort.order === 0;
				this.sortingOperationPriority = sort.priority ? sort.priority : null;
			}
		}
	}

	private subscribeToFormChanges(): void {
		this.form.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				// convert empty strings to the null value
				this.rule[key] = value[key] === '' ? null : value[key];
			});
		});
		this.form.statusChanges.subscribe(this.updateIsFormValid.bind(this));
		this.sortingPriorityForm.statusChanges.subscribe(this.updateIsFormValid.bind(this));
	}

	private updateIsFormValid(): void {
		this.isFormValid = this.form.valid && this.isSortingFormValid;
	}

	public get controls() {
		return this.form.controls;
	}

	public onCancel(): void {
		this.dialogRef.close();
	}

	public async onSave(): Promise<void> {
		this.httpError = '';
		if (this.form.invalid || this.loading) return;
		this.loading = true;
		this.rule.sorting = this.configSorting;
		const logisticsFormula = this.controls['logisticsFormula'].value;
		const saveObservable = this.rule.self ? this.rackingService.updateRule(this.rule) : this.rackingService.createRule(this.rule);

		saveObservable.subscribe({
			next: () => {
				this.saved.emit();
				this.dialogRef.close();
			},
			error: (errorResponse) => {
				this.httpError = `${errorResponse.status}`;
				// Only the API validates the formula, so its reason is shown at the field - unless the
				// formula was edited while saving, since the reason is about the one that was sent.
				const logisticsFormulaError = errorResponse.error?.errors?.logisticsFormula?.[0];
				if (logisticsFormulaError && this.controls['logisticsFormula'].value === logisticsFormula) {
					this.controls['logisticsFormula'].setErrors({ api: logisticsFormulaError });
				}
				this.loading = false;
			},
		});
	}

	public updateConfiguration(): void {
		this.setEnableDisablePriorityFormFields();
		this.updateOrderAndPriorityValues();
		this.updatePriorityFormValidity();
	}

	private get configSorting(): IRackingRuleSortItem[] {
		const sorting: IRackingRuleSortItem[] = [];
		if (this.sortingProfileLength) {
			const priority = this.sortingProfileLengthPriority;
			sorting.push({
				order: this.sortingProfileLengthOrder ? 0 : 1,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/racking/configuration-sorting-strategies/profileLength',
				},
			});
		}
		if (this.sortingProductionSequenceNumber) {
			const priority = this.sortingProductionSequenceNumberPriority;
			sorting.push({
				order: this.sortingProductionSequenceNumberOrder ? 0 : 1,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/racking/configuration-sorting-strategies/productionSequenceNumber',
				},
			});
		}
		if (this.sortingModelIndex) {
			const priority = this.sortingModelIndexPriority;
			sorting.push({
				order: this.sortingModelIndexOrder ? 0 : 1,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/racking/configuration-sorting-strategies/modelIndex',
				},
			});
		}
		if (this.sortingSequenceNumber) {
			const priority = this.sortingSequenceNumberPriority;
			sorting.push({
				order: this.sortingSequenceNumberOrder ? 0 : 1,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/racking/configuration-sorting-strategies/sequenceNumber',
				},
			});
		}
		if (this.sortingComponent) {
			const priority = this.sortingComponentPriority;
			sorting.push({
				order: this.sortingComponentOrder ? 0 : 1,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/racking/configuration-sorting-strategies/component',
				},
			});
		}
		if (this.sortingOperation) {
			const priority = this.sortingOperationPriority;
			sorting.push({
				order: this.sortingOperationOrder ? 0 : 1,
				priority: priority ? Number(priority) : 0,
				type: {
					self: '/racking/configuration-sorting-strategies/operation',
				},
			});
		}
		return sorting;
	}

	/** Huge catcha! If all FormFields in a Form are disabled, the Form.valid returns false. So we iterate over each FromField to check if they are disabled. */
	private get isSortingFormValid(): boolean {
		const sorting = Object.entries(this.sortingPriorityForm.controls);
		for (const [_, control] of sorting) {
			if (control.enabled && control.invalid) {
				return false;
			}
		}
		return true;
	}

	private updateOrderAndPriorityValues(): void {
		if (!this.sortingProfileLength) {
			this.sortingProfileLengthOrder = false;
			this.sortingProfileLengthPriority = null;
		}
		if (!this.sortingProductionSequenceNumber) {
			this.sortingProductionSequenceNumberOrder = false;
			this.sortingProductionSequenceNumberPriority = null;
		}
		if (!this.sortingModelIndex) {
			this.sortingModelIndexOrder = false;
			this.sortingModelIndexPriority = null;
		}
		if (!this.sortingSequenceNumber) {
			this.sortingSequenceNumberOrder = false;
			this.sortingSequenceNumberPriority = null;
		}
		if (!this.sortingComponent) {
			this.sortingComponentOrder = false;
			this.sortingComponentPriority = null;
		}
		if (!this.sortingOperation) {
			this.sortingOperationOrder = false;
			this.sortingOperationPriority = null;
		}
	}

	public updatePriorityFormValidity(): void {
		/**
		 * Form.markAllAsTouched(); form.updateValueAndValidity();
		 *
		 * Will not trigger immediately, so we need to do this instead
		 */
		const sorting = Object.entries(this.sortingPriorityForm.controls);
		for (const [_, control] of sorting) {
			control.markAsTouched({ onlySelf: true });
			control.updateValueAndValidity();
		}
		this.updateSortingErrors();
	}

	private updateSortingErrors(): void {
		this.notuniqueError = false;
		this.profileLengthMaxError = false;
		this.productionSequenceMaxError = false;
		this.modelIndexMaxError = false;
		this.sequenceNumberMaxError = false;
		this.componentMaxError = false;
		this.operationMaxError = false;
		this.updateProfileLengthError();
		this.updateProductionSequenceError();
		this.updateModelIndexError();
		this.updateSequenceNumberError();
		this.updateComponentError();
		this.updateOperationError();
	}

	private updateProfileLengthError(): void {
		const profileLengthControl = this.sortingPriorityForm.get('sortingProfileLengthPriority');
		if (profileLengthControl && profileLengthControl.errors) {
			if (profileLengthControl.errors['max']) {
				this.profileLengthMaxError = true;
			}
			if (profileLengthControl.errors['notunique']) {
				// not unique has higher priority, so turn off max error
				this.profileLengthMaxError = false;
				this.notuniqueError = true;
			}
		}
	}

	private updateProductionSequenceError(): void {
		const sequenceControl = this.sortingPriorityForm.get('sortingProductionSequenceNumberPriority');
		if (sequenceControl && sequenceControl.errors) {
			if (sequenceControl.errors['max']) {
				this.productionSequenceMaxError = true;
			}
			if (sequenceControl.errors['notunique']) {
				// not unique has higher priority, so turn off max error
				this.productionSequenceMaxError = false;
				this.notuniqueError = true;
			}
		}
	}

	private updateModelIndexError(): void {
		const sequenceControl = this.sortingPriorityForm.get('sortingModelIndexPriority');
		if (sequenceControl && sequenceControl.errors) {
			if (sequenceControl.errors['max']) {
				this.modelIndexMaxError = true;
			}
			if (sequenceControl.errors['notunique']) {
				// not unique has higher priority, so turn off max error
				this.modelIndexMaxError = false;
				this.notuniqueError = true;
			}
		}
	}

	private updateSequenceNumberError(): void {
		const sequenceControl = this.sortingPriorityForm.get('sortingSequenceNumberPriority');
		if (sequenceControl && sequenceControl.errors) {
			if (sequenceControl.errors['max']) {
				this.sequenceNumberMaxError = true;
			}
			if (sequenceControl.errors['notunique']) {
				// not unique has higher priority, so turn off max error
				this.sequenceNumberMaxError = false;
				this.notuniqueError = true;
			}
		}
	}

	private updateComponentError(): void {
		const componentControl = this.sortingPriorityForm.get('sortingComponentPriority');
		if (componentControl && componentControl.errors) {
			if (componentControl.errors['max']) {
				this.componentMaxError = true;
			}
			if (componentControl.errors['notunique']) {
				// not unique has higher priority, so turn off max error
				this.componentMaxError = false;
				this.notuniqueError = true;
			}
		}
	}

	private updateOperationError(): void {
		const operationControl = this.sortingPriorityForm.get('sortingOperationPriority');
		if (operationControl && operationControl.errors) {
			if (operationControl.errors['max']) {
				this.operationMaxError = true;
			}
			if (operationControl.errors['notunique']) {
				// not unique has higher priority, so turn off max error
				this.operationMaxError = false;
				this.notuniqueError = true;
			}
		}
	}

	public get sortingPriorityControls() {
		return this.sortingPriorityForm.controls;
	}

	private get sortingPriorityFormValidators(): FormValidators {
		return [this.sortingNotUnique.bind(this), Validators.max(99999)];
	}

	private sortingNotUnique(target: FormControl): { notunique: boolean } | null {
		return this.notUnique(target, this.sortingPriorityForm);
	}

	/**
	 * This could be more simple with only two Form Controls, but we might add more, so we keep the scalable version
	 *
	 * @param target The current FormControl that is being validated
	 * @param form The Sorting Form
	 * @returns Null if values are unique, otherwise an Error Object
	 */
	private notUnique(target: FormControl, form: FormGroup): { notunique: boolean } | null {
		if (!target.value) {
			return null;
		}
		const targetValue = String(target.value);
		const controls = Object.entries(form.controls);
		for (const [_, control] of controls) {
			if (control !== target) {
				const controlValue = String(control.value);
				if (controlValue === '' && targetValue === '') {
					return null;
				}
				if (controlValue === targetValue) {
					return {
						notunique: true,
					};
				}
			}
		}
		return null;
	}
}

function areMachineAndMaterialValid(machine: AbstractControl, material: AbstractControl): boolean {
	if (machine.value === '' && material.value === '') {
		return true;
	}
	if (machine.value !== '' && material.value !== '') {
		return true;
	}
	return false;
}

function validateAluminium(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const form = control.parent;
		if (form) {
			const machine: AbstractControl = form.get('machine');
			const material: AbstractControl = form.get('material');
			if (machine && material) {
				if (areMachineAndMaterialValid(machine, material)) {
					machine.setErrors(null);
					material.setErrors(null);
					return null;
				}
				return { invalid: true };
			}
		}
		return null;
	};
}
